import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { parseDateOnly } from '../data/sampleData';

// Franchise agreement (FA) amendments — a tab of the Exception ledger
// (Bettina, 2026-10-21). Permission from AFI for a franchisee to serve named
// locations outside its territory; audits clear residents of those locations
// as "Cleared by SPC/IR/FA". Admins add, edit and terminate; reviewers view.

interface Location { id?: number; street_address: string; city: string | null; state: string | null; zip: string; lat?: number | null; lng?: number | null }
interface Amendment {
  id: number;
  receiving_af: string;
  title: string;
  effective_from: string;
  expires_on: string | null;
  terms_note: string | null;
  document_ref: string | null;
  status: 'active' | 'terminated';
  expired: boolean;                 // active but past its end date — no longer in force
  scope_kind: ScopeKind;            // what the amendment permits
  applies_to: 'named_franchisee' | 'any_franchisee';
  ends_when_territory_opens: boolean;
  territory_status: string | null;
  territory_open: boolean;
  zips: string[];
  payers: string[];
  clients: { site_number: number; axiscare_client_id: string; name: string | null }[];
  documents: { id: number; filename: string; size_bytes: number; uploaded_at: string; uploaded_by: string | null }[];
  terminated_on: string | null;
  termination_reason: string | null;
  terminated_by: string | null;
  created_by: string | null;
  locations: Location[];
  residents_matched: number;
  residents_served_90d: number;
  last_service_at_facility: string | null;
  days_since_last_service: number | null;
  no_service_termination_days: number | null;   // only some amendments have this term
  no_service_warning: boolean;
  near_miss_count: number;
}
type ScopeKind = 'address' | 'zip' | 'payer' | 'client';
const SCOPE_LABEL: Record<ScopeKind, string> = {
  address: 'Specific address(es)', zip: 'Zip code(s)', payer: 'Payer type(s)', client: 'Client(s)',
};

interface Resident {
  site_number: number; client_name: string | null; street_address: string | null; zip: string | null;
  client_status: string | null; last_visit: string | null; visits_90d: number; location: string; reason: string;
}

// "FA - AF-131 - 09112024": FA, the AF#, the effective date as MMDDYYYY
// (Bettina, 2026-10-21) — the name of every FA exception.
export const faLabel = (a: { receiving_af: string; effective_from: string | null }) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(a.effective_from ?? '');
  return `FA - ${a.receiving_af} - ${m ? `${m[2]}${m[3]}${m[1]}` : 'no date'}`;
};

const day = (v: string | null) => {
  const d = parseDateOnly(v);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};
const when = (v: string | null) => (v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const errText = (e: unknown, fallback: string) => {
  const body = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
  return body?.error ?? (e instanceof Error ? e.message : fallback);
};

export function FaAmendments({ role }: { role: 'admin' | 'reviewer' }) {
  const [items, setItems] = useState<Amendment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Amendment | 'new' | null>(null);
  const [terminating, setTerminating] = useState<Amendment | null>(null);
  const [residentsOf, setResidentsOf] = useState<Amendment | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const isAdmin = role === 'admin';

  const load = () => api<{ amendments: Amendment[] }>('/api/franchise-amendments')
    .then((d) => setItems(d.amendments)).catch((e) => setError(errText(e, 'Failed to load')));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 820 }}>
          Franchise agreement amendments that let a franchisee serve outside its territory. Each one permits one kind of thing:
          <strong> specific address(es)</strong> (any unit at the address; not the rest of the zip — near misses are listed for review),
          <strong> zip code(s)</strong> (the client's main service zip), <strong> payer type(s)</strong> (every visit payer must be listed;
          mixed payers go to review) or <strong> client(s)</strong>. Audits clear what an amendment covers as <strong>Cleared by SPC/IR/FA</strong>.
        </div>
        {isAdmin && <button className="btn btn-primary" onClick={() => setEditing('new')}><Icon name="plus" size={12} /> Add amendment</button>}
      </div>

      {notice && <div className="card card-pad" style={{ fontSize: 12.5, marginBottom: 12, background: 'var(--info-soft)' }}>{notice}</div>}
      {error && <div className="card card-pad c-warn" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {!items && !error && <div className="card card-pad muted" style={{ fontSize: 13 }}>Loading…</div>}
      {items && items.length === 0 && <div className="card card-pad muted" style={{ fontSize: 13 }}>No amendments recorded yet.</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items?.map((a) => (
          <div key={a.id} className="card card-pad" style={{ opacity: a.status === 'terminated' ? 0.75 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong className="mono" style={{ fontSize: 14 }}>{faLabel(a)}</strong>
                  <span style={{ fontSize: 13.5 }}>{a.title}</span>
                  <span className={a.status === 'active' && !a.expired ? 'c-ok' : 'c-warn'} style={{ fontSize: 12, fontWeight: 600 }}>
                    {a.status === 'terminated' ? `Terminated ${day(a.terminated_on)}` : a.expired ? `Expired ${day(a.expires_on)}` : 'Active'}
                  </span>
                </div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Effective {day(a.effective_from)} · {a.expires_on ? `expires ${day(a.expires_on)}` : 'no fixed end date'}
                  {a.no_service_termination_days != null && <> · ends after {a.no_service_termination_days} days with no service</>}
                  {a.document_ref && <> · {/^https?:\/\//i.test(a.document_ref)
                    ? <a href={a.document_ref} target="_blank" rel="noreferrer">SharePoint document</a>
                    : <>document: {a.document_ref}</>}</>}
                  {a.created_by && <> · entered by {a.created_by}</>}
                </div>
                <div style={{ fontSize: 12, marginTop: 6 }}>
                  <span className="muted">Who:</span>{' '}
                  {a.applies_to === 'any_franchisee'
                    ? <strong>any neighbouring franchisee, inside {a.receiving_af}&apos;s territory</strong>
                    : <strong>{a.receiving_af}, outside its territory</strong>}
                  {a.applies_to === 'any_franchisee' && a.ends_when_territory_opens && (
                    <span className="muted"> · until {a.receiving_af} opens (Zoho: {a.territory_status ?? 'unknown'})</span>
                  )}
                  {' · '}<span className="muted">Permits:</span> <strong>{SCOPE_LABEL[a.scope_kind ?? 'address']}</strong>
                </div>
                {a.scope_kind === 'zip' && <div className="mono" style={{ fontSize: 12.5 }}>{a.zips.join(', ')}</div>}
                {a.scope_kind === 'payer' && <div style={{ fontSize: 12.5 }}>{a.payers.join(' · ')} <span className="muted" style={{ fontSize: 11.5 }}>(every visit in the window must be one of these; mixed payers go to review)</span></div>}
                {a.scope_kind === 'client' && <div style={{ fontSize: 12.5 }}>{a.clients.map((c) => c.name ?? c.axiscare_client_id).join(' · ')}</div>}
                <div style={{ fontSize: 12.5, marginTop: 2 }}>
                  {(a.scope_kind ?? 'address') === 'address' && a.locations.map((l) => (
                    <div key={l.id ?? l.street_address} className="mono">
                      {l.street_address}{l.city ? `, ${l.city}` : ''}{l.state ? `, ${l.state}` : ''} {l.zip}
                      {l.lat == null && <span className="c-warn" style={{ fontSize: 11 }}> · not mapped</span>}
                    </div>
                  ))}
                </div>
                {a.terms_note && <div className="muted" style={{ fontSize: 12, marginTop: 6, whiteSpace: 'pre-wrap' }}>{a.terms_note}</div>}
                <Documents a={a} isAdmin={isAdmin} onChanged={load} />
                {a.status === 'terminated' && (
                  <div className="c-warn" style={{ fontSize: 12.5, marginTop: 6 }}>
                    Termination reason: {a.termination_reason}{a.terminated_by ? ` (marked by ${a.terminated_by})` : ''}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button className="btn btn-sm" onClick={() => setResidentsOf(a)}>Residents</button>
                {isAdmin && <button className="btn btn-sm" onClick={() => setEditing(a)}>Edit</button>}
                {isAdmin && a.status === 'active' && <button className="btn btn-sm" onClick={() => setTerminating(a)}>Mark terminated</button>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 12.5, flexWrap: 'wrap' }}>
              <span><strong>{a.residents_matched}</strong> {(a.scope_kind ?? 'address') === 'address' ? `resident${a.residents_matched === 1 ? '' : 's'} on file` : `covered client${a.residents_matched === 1 ? '' : 's'}`}</span>
              <span><strong>{a.residents_served_90d}</strong> served in the last 90 days</span>
              <span>Last service {(a.scope_kind ?? 'address') === 'address' ? 'at the location' : 'to a covered client'}: <strong>{when(a.last_service_at_facility)}</strong>
                {a.days_since_last_service != null && <span className="muted"> ({a.days_since_last_service} days ago)</span>}</span>
              {a.near_miss_count > 0 && <span className="c-warn">{a.near_miss_count} near miss{a.near_miss_count === 1 ? '' : 'es'} to check</span>}
            </div>
            {a.status === 'active' && a.applies_to === 'any_franchisee' && a.ends_when_territory_opens && a.territory_open && (
              <div className="c-warn" style={{ fontSize: 12.5, marginTop: 8 }}>
                ⚠️ {a.receiving_af} is now open (Zoho: {a.territory_status ?? '—'}), so this permission has ended and audits no longer clear under it.
                Mark it terminated to record the end date.
              </div>
            )}
            {a.status === 'active' && a.no_service_warning && (
              <div className="c-warn" style={{ fontSize: 12.5, marginTop: 8 }}>
                ⚠️ {a.last_service_at_facility ? `No covered client served in ${a.days_since_last_service} days.` : 'No service to a covered client on record.'}{' '}
                Under this amendment&apos;s terms, {a.no_service_termination_days} days without service terminates it — review, and mark it terminated if that applies.
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <EditModal
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(warnings) => { setEditing(null); setNotice(warnings.length ? warnings.join(' ') : 'Saved. The next audit of this state applies it.'); load(); }}
        />
      )}
      {terminating && (
        <TerminateModal a={terminating} onClose={() => setTerminating(null)} onDone={() => { setTerminating(null); setNotice('Marked terminated. From the next audit, these residents are evaluated normally again.'); load(); }} />
      )}
      {residentsOf && <ResidentsModal a={residentsOf} onClose={() => setResidentsOf(null)} />}
    </div>
  );
}

// Signed amendment PDFs, stored in the tool (Bettina, 2026-10-21).
function Documents({ a, isAdmin, onChanged }: { a: Amendment; isAdmin: boolean; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = async (file: File | undefined) => {
    if (!file) return;
    if (file.type && file.type !== 'application/pdf') { setError('Choose a PDF file.'); return; }
    if (file.size > 15 * 1024 * 1024) { setError('That PDF is over 15 MB.'); return; }
    setBusy(true); setError(null);
    try {
      const res = await fetch(`/api/franchise-amendments/${a.id}/documents?filename=${encodeURIComponent(file.name)}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/pdf' }, body: file,
      });
      if (!res.ok) { const b = await res.json().catch(() => null); throw new Error(b?.error ?? `Upload failed (${res.status})`); }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (docId: number, name: string) => {
    if (!confirm(`Remove "${name}" from ${faLabel(a)}?`)) return;
    try { await api(`/api/franchise-amendments/${a.id}/documents/${docId}`, { method: 'DELETE' }); onChanged(); }
    catch (e) { setError(errText(e, 'Could not remove')); }
  };
  if (!a.documents.length && !isAdmin) return null;
  return (
    <div style={{ marginTop: 8, fontSize: 12.5 }}>
      <span className="muted">Signed amendment: </span>
      {a.documents.length === 0 && <span className="muted">no PDF uploaded</span>}
      {a.documents.map((d, i) => (
        <span key={d.id}>
          {i > 0 && ' · '}
          <a href={`/api/franchise-amendments/${a.id}/documents/${d.id}`} target="_blank" rel="noreferrer">{d.filename}</a>
          <span className="faint" style={{ fontSize: 11 }}> ({Math.max(1, Math.round(d.size_bytes / 1024))} KB{d.uploaded_by ? ` · ${d.uploaded_by}` : ''})</span>
          {isAdmin && <button className="btn btn-ghost btn-sm" style={{ height: 20, padding: '0 4px' }} title="Remove" onClick={() => remove(d.id, d.filename)}><Icon name="close" size={10} /></button>}
        </span>
      ))}
      {isAdmin && (
        <label className="btn btn-sm" style={{ marginLeft: 8, cursor: busy ? 'default' : 'pointer' }}>
          <Icon name="plus" size={11} /> {busy ? 'Uploading…' : 'Upload PDF'}
          <input type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} disabled={busy}
                 onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ''; }} />
        </label>
      )}
      {error && <div className="c-warn" style={{ marginTop: 4 }}>{error}</div>}
    </div>
  );
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 8, padding: 18, width: wide ? '92vw' : 640, maxWidth: wide ? 1200 : '92vw', maxHeight: '88vh', overflow: 'auto', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="close" size={12} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 };

function EditModal({ initial, onClose, onSaved }: { initial: Amendment | null; onClose: () => void; onSaved: (warnings: string[]) => void }) {
  const [af, setAf] = useState(initial?.receiving_af ?? '');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [from, setFrom] = useState(initial?.effective_from ?? '');
  const [until, setUntil] = useState(initial?.expires_on ?? '');
  const [noSvcOn, setNoSvcOn] = useState(initial?.no_service_termination_days != null);
  const [noSvcDays, setNoSvcDays] = useState(String(initial?.no_service_termination_days ?? 90));
  const [doc, setDoc] = useState(initial?.document_ref ?? '');
  const [terms, setTerms] = useState(initial?.terms_note ?? '');
  const [locs, setLocs] = useState<Location[]>(initial?.locations.length ? initial.locations : [{ street_address: '', city: '', state: '', zip: '' }]);
  const [kind, setKind] = useState<ScopeKind>(initial?.scope_kind ?? 'address');
  const [appliesTo, setAppliesTo] = useState<'named_franchisee' | 'any_franchisee'>(initial?.applies_to ?? 'named_franchisee');
  const [endsOnOpen, setEndsOnOpen] = useState(initial ? !!initial.ends_when_territory_opens : false);
  const anyFr = appliesTo === 'any_franchisee';
  const [zipText, setZipText] = useState((initial?.zips ?? []).join(', '));
  const [payers, setPayers] = useState<string[]>(initial?.payers ?? []);
  const [payerOptions, setPayerOptions] = useState<{ payer: string; visits: number }[] | null>(null);
  const [payerExtra, setPayerExtra] = useState('');
  const [clients, setClients] = useState<{ site_number: number; axiscare_client_id: string; name: string | null }[]>(initial?.clients ?? []);
  const [clientQ, setClientQ] = useState('');
  const [clientHits, setClientHits] = useState<{ site_number: number; axiscare_client_id: string; name: string | null; zip: string | null; status: string | null }[]>([]);
  const afOk = /^AF-\d+(\.\d+)?$/i.test(af.trim());
  useEffect(() => {
    if (kind !== 'payer' || !afOk) { setPayerOptions(null); return; }
    api<{ payers: { payer: string; visits: number }[] }>(`/api/franchise-amendments/picker/payers?af=${encodeURIComponent(af.trim().toUpperCase())}`)
      .then((d) => setPayerOptions(d.payers)).catch(() => setPayerOptions([]));
  }, [kind, af, afOk]);
  useEffect(() => {
    if (kind !== 'client' || !afOk || clientQ.trim().length < 2) { setClientHits([]); return; }
    const t = setTimeout(() => {
      api<{ clients: typeof clientHits }>(`/api/franchise-amendments/picker/clients?af=${encodeURIComponent(af.trim().toUpperCase())}&q=${encodeURIComponent(clientQ.trim())}`)
        .then((d) => setClientHits(d.clients)).catch(() => setClientHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [kind, af, afOk, clientQ]);
  const zipList = [...new Set(zipText.split(/[^0-9]+/).filter((z) => /^\d{5}$/.test(z)))];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setLoc = (i: number, k: keyof Location, v: string) => setLocs((p) => p.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body = {
        receiving_af: af.trim().toUpperCase(), title: title.trim(), effective_from: from, expires_on: until || null,
        no_service_termination_days: noSvcOn && Number(noSvcDays) > 0 ? Math.round(Number(noSvcDays)) : null,
        document_ref: doc.trim() || null, terms_note: terms.trim() || null,
        applies_to: appliesTo,
        ends_when_territory_opens: anyFr && endsOnOpen,
        scope_kind: kind,
        zips: kind === 'zip' ? zipList : [],
        payers: kind === 'payer' ? payers : [],
        clients: kind === 'client' ? clients.map((c) => ({ site_number: c.site_number, axiscare_client_id: c.axiscare_client_id })) : [],
        locations: kind !== 'address' ? [] : locs.filter((l) => l.street_address.trim()).map((l) => ({
          street_address: l.street_address.trim(), city: (l.city ?? '').trim() || null,
          state: (l.state ?? '').trim().toUpperCase() || null, zip: l.zip.trim(),
        })),
      };
      const r = await api<{ warnings: string[] }>(initial ? `/api/franchise-amendments/${initial.id}` : '/api/franchise-amendments', { method: initial ? 'PUT' : 'POST', body });
      onSaved(r.warnings ?? []);
    } catch (e) {
      setError(errText(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={initial ? `Edit ${faLabel(initial)}` : 'Add franchise agreement amendment'} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ ...field, gridColumn: '1 / -1' }}>
          <span className="muted">Who is permitted?</span>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
            <input type="radio" name="fa-who" checked={!anyFr} onChange={() => setAppliesTo('named_franchisee')} />
            This franchisee, to serve <strong>outside</strong> its territory (e.g. AF-131 at a facility)
          </label>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
            <input type="radio" name="fa-who" checked={anyFr} onChange={() => { setAppliesTo('any_franchisee'); if (kind === 'payer' || kind === 'client') setKind('zip'); if (!initial) setEndsOnOpen(true); }} />
            Any neighbouring franchisee, to serve <strong>inside</strong> this franchisee&apos;s territory (e.g. until it opens)
          </label>
        </div>
        <label style={field}><span className="muted">{anyFr ? 'Territory giving permission (AF#)' : 'Franchisee granted permission (AF#)'}</span><input className="mono" value={af} onChange={(e) => setAf(e.target.value)} placeholder={anyFr ? 'AF-367' : 'AF-131'} /></label>
        <label style={field}><span className="muted">Name (facility, program or description)</span><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. the senior living community" /></label>
        <label style={field}><span className="muted">Effective date</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label style={field}><span className="muted">Expires (leave blank if no fixed end)</span><input type="date" value={until ?? ''} onChange={(e) => setUntil(e.target.value)} /></label>
        {anyFr && (
          <label style={{ ...field, gridColumn: '1 / -1', flexDirection: 'row', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={endsOnOpen} onChange={(e) => setEndsOnOpen(e.target.checked)} />
            Ends automatically when this territory opens (Zoho status Open, or its Operational Date passes)
          </label>
        )}
        <div style={{ ...field, gridColumn: '1 / -1', flexDirection: 'row', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
          <input type="checkbox" checked={noSvcOn} onChange={(e) => setNoSvcOn(e.target.checked)} id="fa-nosvc" />
          <label htmlFor="fa-nosvc">This amendment ends if no covered client is served for</label>
          <input type="number" min={1} value={noSvcDays} onChange={(e) => setNoSvcDays(e.target.value)} disabled={!noSvcOn} style={{ width: 70 }} />
          <span>days</span>
          <span className="muted" style={{ fontSize: 11.5 }}>(only if the amendment says so — the page then warns; it never terminates on its own)</span>
        </div>
        <label style={{ ...field, gridColumn: '1 / -1' }}><span className="muted">SharePoint link to the signed amendment (optional — or upload the PDF on the amendment after saving)</span><input value={doc} onChange={(e) => setDoc(e.target.value)} placeholder="https://…sharepoint.com/…" /></label>
        <label style={{ ...field, gridColumn: '1 / -1' }}><span className="muted">Terms / termination conditions (optional)</span><textarea rows={3} value={terms} onChange={(e) => setTerms(e.target.value)} /></label>
      </div>
      <div style={{ marginTop: 14, fontSize: 12, fontWeight: 600 }}>What does the amendment permit?</div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6, fontSize: 12.5 }}>
        {(Object.keys(SCOPE_LABEL) as ScopeKind[]).filter((k) => !anyFr || k === 'address' || k === 'zip').map((k) => (
          <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
            <input type="radio" name="fa-kind" checked={kind === k} onChange={() => setKind(k)} /> {SCOPE_LABEL[k]}
          </label>
        ))}
      </div>
      {kind === 'address' && (<>
      <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600 }}>Location addresses <span className="muted" style={{ fontWeight: 400 }}>(street without unit numbers — every unit at the address is included)</span></div>
      {locs.map((l, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.5fr 0.8fr auto', gap: 6, marginTop: 6 }}>
          <input placeholder="Street (e.g. 3581 Braselton Hwy)" value={l.street_address} onChange={(e) => setLoc(i, 'street_address', e.target.value)} />
          <input placeholder="City" value={l.city ?? ''} onChange={(e) => setLoc(i, 'city', e.target.value)} />
          <input placeholder="ST" maxLength={2} value={l.state ?? ''} onChange={(e) => setLoc(i, 'state', e.target.value)} />
          <input placeholder="Zip" className="mono" value={l.zip} onChange={(e) => setLoc(i, 'zip', e.target.value)} />
          <button className="btn btn-ghost btn-sm" onClick={() => setLocs((p) => p.filter((_, j) => j !== i))} disabled={locs.length === 1}><Icon name="close" size={11} /></button>
        </div>
      ))}
      <button className="btn btn-sm btn-ghost" style={{ marginTop: 6 }} onClick={() => setLocs((p) => [...p, { street_address: '', city: '', state: '', zip: '' }])}><Icon name="plus" size={11} /> Add address</button>
      </>)}
      {kind === 'zip' && (
        <label style={{ ...field, marginTop: 10 }}>
          <span className="muted">Zip codes (separate with commas or spaces). Clears when the zip the audit evaluates — the client's main service zip — is one of these.</span>
          <input className="mono" value={zipText} onChange={(e) => setZipText(e.target.value)} placeholder="30019, 30017" />
          {zipList.length > 0 && <span className="muted">{zipList.length} zip{zipList.length === 1 ? '' : 's'}: {zipList.join(', ')}</span>}
        </label>
      )}
      {kind === 'payer' && (
        <div style={{ marginTop: 10, fontSize: 12.5 }}>
          <div className="muted" style={{ fontSize: 12 }}>Payers as AxisCare names them. Clears only when every visit payer in the window is one of these; mixed payers or no payer data go to manual review.</div>
          {!afOk && <div className="c-warn" style={{ fontSize: 12, marginTop: 6 }}>Enter the AF# first to list its payers.</div>}
          {afOk && payerOptions === null && <div className="muted" style={{ marginTop: 6 }}>Loading payers…</div>}
          {afOk && payerOptions && payerOptions.length === 0 && <div className="muted" style={{ marginTop: 6 }}>No payers on this franchisee's visits in the last year — add one by name below.</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 6 }}>
            {[...new Set([...(payerOptions ?? []).map((p) => p.payer), ...payers])].map((p) => (
              <label key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input type="checkbox" checked={payers.includes(p)} onChange={(e) => setPayers((prev) => e.target.checked ? [...prev, p] : prev.filter((x) => x !== p))} />
                {p}{payerOptions?.find((o) => o.payer === p) && <span className="faint" style={{ fontSize: 11 }}> ({payerOptions.find((o) => o.payer === p)!.visits} visits)</span>}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input value={payerExtra} onChange={(e) => setPayerExtra(e.target.value)} placeholder="Other payer name, exactly as in AxisCare" style={{ flex: 1 }} />
            <button className="btn btn-sm" disabled={!payerExtra.trim()} onClick={() => { const v = payerExtra.trim(); setPayers((p) => (p.includes(v) ? p : [...p, v])); setPayerExtra(''); }}>Add</button>
          </div>
        </div>
      )}
      {kind === 'client' && (
        <div style={{ marginTop: 10, fontSize: 12.5 }}>
          <div className="muted" style={{ fontSize: 12 }}>Pick the clients from this franchisee's AxisCare records.</div>
          {!afOk && <div className="c-warn" style={{ fontSize: 12, marginTop: 6 }}>Enter the AF# first to search its clients.</div>}
          {clients.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {clients.map((c) => (
                <span key={`${c.site_number}:${c.axiscare_client_id}`} className="chip" style={{ cursor: 'default' }}>
                  {c.name ?? c.axiscare_client_id}{' '}
                  <span className="chip-x" style={{ cursor: 'pointer' }} onClick={() => setClients((p) => p.filter((x) => !(x.site_number === c.site_number && x.axiscare_client_id === c.axiscare_client_id)))}>×</span>
                </span>
              ))}
            </div>
          )}
          <input value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="Search by client name (2+ letters)" style={{ marginTop: 8, width: '100%' }} disabled={!afOk} />
          {clientHits.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4, maxHeight: 180, overflow: 'auto' }}>
              {clientHits.map((h) => {
                const added = clients.some((c) => c.site_number === h.site_number && c.axiscare_client_id === h.axiscare_client_id);
                return (
                  <div key={`${h.site_number}:${h.axiscare_client_id}`} onClick={() => { if (!added) setClients((p) => [...p, { site_number: h.site_number, axiscare_client_id: h.axiscare_client_id, name: h.name }]); }}
                       style={{ padding: '5px 8px', cursor: added ? 'default' : 'pointer', opacity: added ? 0.5 : 1, fontSize: 12 }}>
                    {h.name ?? '(no name on file)'} <span className="faint">· {h.zip ?? '—'} · {h.status ?? '—'}{added ? ' · added' : ''}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {error && <div className="c-warn" style={{ fontSize: 12.5, marginTop: 10 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !af || !title || !from}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

function TerminateModal({ a, onClose, onDone }: { a: Amendment; onClose: () => void; onDone: () => void }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await api(`/api/franchise-amendments/${a.id}/terminate`, { method: 'POST', body: { terminated_on: date, reason: reason.trim() } });
      onDone();
    } catch (e) {
      setError(errText(e, 'Could not mark terminated'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={`Mark ${faLabel(a)} terminated`} onClose={onClose}>
      <div style={{ fontSize: 12.5, marginBottom: 10 }}>
        <strong>{a.title}</strong> · <span className="mono">{a.receiving_af}</span>. From the next audit, its residents are evaluated as normal encroachment again.
      </div>
      <label style={field}><span className="muted">Terminated on</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
      <label style={{ ...field, marginTop: 10 }}><span className="muted">Reason (required)</span>
        <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. No client served at the facility for 90 days; FA expired; facility contract ended" />
      </label>
      {error && <div className="c-warn" style={{ fontSize: 12.5, marginTop: 10 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving || reason.trim().length < 3}>{saving ? 'Saving…' : 'Mark terminated'}</button>
      </div>
    </Modal>
  );
}

function ResidentsModal({ a, onClose }: { a: Amendment; onClose: () => void }) {
  const [data, setData] = useState<{ matched: Resident[]; near: Resident[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ matched: Resident[]; near: Resident[] }>(`/api/franchise-amendments/${a.id}/residents`)
      .then(setData).catch((e) => setError(errText(e, 'Failed to load')));
  }, [a.id]);
  const table = (rows: Resident[], showReason: boolean) => (
    <table className="tbl">
      <thead><tr><th>Client</th><th>Home address</th><th>Zip</th><th>Status</th><th>Last visit</th><th style={{ textAlign: 'right' }}>Visits (90d)</th>{showReason && <th>Why it is a near miss</th>}</tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={`${r.site_number}:${r.client_name}:${r.street_address}`}>
          <td>{r.client_name ?? <span className="muted">(no name on file)</span>}</td>
          <td>{r.street_address ?? '—'}</td>
          <td className="mono">{r.zip ?? '—'}</td>
          <td>{r.client_status ?? '—'}</td>
          <td>{when(r.last_visit)}</td>
          <td className="num mono" style={{ textAlign: 'right' }}>{r.visits_90d}</td>
          {showReason && <td className="muted" style={{ fontSize: 11.5 }}>{r.reason}</td>}
        </tr>
      ))}</tbody>
    </table>
  );
  return (
    <Modal title={`${faLabel(a)} · ${a.title} · covered clients`} onClose={onClose} wide>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>From the client records already in the tool (home address on file).</div>
      {error && <div className="c-warn" style={{ fontSize: 13 }}>{error}</div>}
      {!data && !error && <div className="muted" style={{ fontSize: 13 }}>Loading…</div>}
      {data && (
        <>
          <h4 style={{ margin: '4px 0 6px', fontSize: 13 }}>Matched — covered by the amendment ({data.matched.length})</h4>
          {data.matched.length ? table(data.matched, false) : <div className="muted" style={{ fontSize: 12.5 }}>No client records at these addresses.</div>}
          <h4 style={{ margin: '16px 0 6px', fontSize: 13 }} className={data.near.length ? 'c-warn' : ''}>Near misses — NOT covered; check ({data.near.length})</h4>
          {data.near.length ? table(data.near, true) : <div className="muted" style={{ fontSize: 12.5 }}>None.</div>}
        </>
      )}
    </Modal>
  );
}
